// --- Dependencies -------------------------------------------------------------------------------
import { useNavigate } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import type { TyAccount } from "@/hooks/useAuth/types";
import usePopup from "@/hooks/usePopup";
import useStore from "@/hooks/useStore";
import useTranslation from "@/hooks/useTranslation";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    onClose: () => void;
};

// --- Logic --------------------------------------------------------------------------------------

export default function ProfilePopup({ onClose }: Props) {
    const t = useTranslation();
    const navigate = useNavigate();
    const { addComp, removeComp } = usePopup();

    const client = useAuth((state) => state.client);
    const accounts = useAuth((state) => state.accounts);
    const switchAccount = useAuth((state) => state.switchAccount);
    const startAddingAccount = useAuth((state) => state.startAddingAccount);
    const storeId = useStore((state) => state.storeId);
    const clearStore = useStore((state) => state.clearStore);

    const isOwner = storeId ? (client?.owner?.includes(storeId) ?? false) : false;
    const others = accounts.filter((account) => account.client._id !== client?._id);

    const handleVisitProfile = () => {
        onClose();
        navigate("/profile");
    };

    const handleAddAccount = () => {
        onClose();
        startAddingAccount();
        navigate("/login");
    };

    const confirmSwitch = (account: TyAccount) => {
        removeComp("confirm-switch-account");
        addComp({
            name: "confirm-switch-account",
            Component: ConfirmPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
            details: {
                message: `${t.profilePopup.switchConfirmMessage} ${account.client.name}`,
                icon: "/icons/client-icon.svg",
                variant: "primary",
                confirmLabel: t.profilePopup.switchConfirmAction,
                cancelLabel: t.profilePopup.switchCancel,
                onConfirm: () => {
                    switchAccount(account.client._id);
                    // The stores belong to the previous identity, so the whole dashboard restarts clean.
                    clearStore();
                    window.location.replace("/");
                },
            },
        });
    };

    const handleSwitch = (account: TyAccount) => {
        onClose();
        confirmSwitch(account);
    };

    return (
        <div className="profile-popup">
            {/* --- Active account ------------------------------------------------------------- */}
            <button type="button" className="profile-popup-active" onClick={handleVisitProfile}>
                <div className="profile-popup-active-avatar">
                    {client?.picture ? (
                        <img src={client.picture} alt={client.name} />
                    ) : (
                        <img className="profile-popup-avatar-placeholder" src="/icons/client-small-placeholder-icon.svg" alt={client?.name ?? ""} />
                    )}
                </div>
                <div className="profile-popup-active-info">
                    <p className="profile-popup-name">{client?.name ?? "—"}</p>
                    <p className="profile-popup-phone" dir="ltr">
                        {client?.phone ?? ""}
                    </p>
                    <span className={`profile-popup-role${isOwner ? "" : " profile-popup-role--staff"}`}>
                        {isOwner ? t.profilePopup.owner : t.profilePopup.staff}
                    </span>
                </div>
                <span className="profile-popup-active-link">{t.profilePopup.viewProfile}</span>
            </button>

            {/* --- Other signed-in accounts ---------------------------------------------------- */}
            {others.length > 0 && (
                <>
                    <p className="profile-popup-section-label">{t.profilePopup.otherAccounts}</p>
                    <div className="profile-popup-others">
                        {others.map((account) => (
                            <button key={account.client._id} type="button" className="profile-popup-other" onClick={() => handleSwitch(account)}>
                                <div className="profile-popup-other-avatar">
                                    {account.client.picture ? (
                                        <img src={account.client.picture} alt={account.client.name} />
                                    ) : (
                                        <img
                                            className="profile-popup-avatar-placeholder"
                                            src="/icons/client-small-placeholder-icon.svg"
                                            alt={account.client.name}
                                        />
                                    )}
                                </div>
                                <div className="profile-popup-other-info">
                                    <p className="profile-popup-other-name">{account.client.name}</p>
                                    <p className="profile-popup-other-phone" dir="ltr">
                                        {account.client.phone}
                                    </p>
                                </div>
                            </button>
                        ))}
                    </div>
                </>
            )}

            <div className="profile-popup-divider" />

            <button type="button" className="profile-popup-add" onClick={handleAddAccount}>
                <img src="/icons/plus-gray-icon.svg" alt="" width={12} height={12} />
                <span>{t.profilePopup.addAccount}</span>
            </button>
        </div>
    );
}
